import { create } from 'zustand';

import { billingApi, CurrencyContextResponse } from '@/lib/api/billing';

const USD_FALLBACK: CurrencyContextResponse = {
  displayCurrency: 'usd',
  locked: false,
  availableCurrencies: ['usd'],
  rates: { usd: 1 },
};

type GetToken = () => Promise<string | null>;

interface CurrencyState {
  context: CurrencyContextResponse;
  loading: boolean;
  /** Workspace the current context was fetched for. */
  fetchedWorkspaceId: string | null;
  _inFlight: Promise<void> | null;
  ensureLoaded: (getToken: GetToken, workspaceId: string | null) => Promise<void>;
  /** Force a refetch for the current workspace (e.g. after a top-up pins the currency). */
  refresh: (getToken: GetToken) => Promise<void>;
  setPreferredCurrency: (getToken: GetToken, currency: string) => Promise<void>;
  reset: () => void;
}

/**
 * Module-global currency context — a single shared source of truth for every
 * billing/cloud surface. Deduplicates fetches (one Stripe customers.retrieve
 * per workspace) and refetches when the active workspace changes.
 */
export const useCurrencyStore = create<CurrencyState>((set, get) => ({
  context: USD_FALLBACK,
  loading: true,
  fetchedWorkspaceId: null,
  _inFlight: null,

  ensureLoaded: async (getToken, workspaceId) => {
    const s = get();
    if (s.fetchedWorkspaceId === workspaceId && !s._inFlight) return; // fresh for this workspace
    if (s._inFlight) return s._inFlight; // dedupe concurrent callers

    const p = (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const data = await billingApi.getCurrencyContext(token);
        set({ context: data, fetchedWorkspaceId: workspaceId });
      } catch {
        set({ context: USD_FALLBACK, fetchedWorkspaceId: workspaceId });
      } finally {
        set({ loading: false, _inFlight: null });
      }
    })();

    set({ loading: true, _inFlight: p });
    return p;
  },

  refresh: async (getToken) => {
    const workspaceId = get().fetchedWorkspaceId;
    const p = (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const data = await billingApi.getCurrencyContext(token);
        set({ context: data, fetchedWorkspaceId: workspaceId });
      } catch {
        // Keep the existing context on refresh failure — don't downgrade a
        // known currency back to USD.
      } finally {
        set({ _inFlight: null });
      }
    })();
    set({ _inFlight: p });
    return p;
  },

  setPreferredCurrency: async (getToken, currency) => {
    const token = await getToken();
    if (!token) return;
    const data = await billingApi.setPreferredCurrency(token, currency);
    set({ context: data });
  },

  reset: () => set({ context: USD_FALLBACK, loading: true, fetchedWorkspaceId: null, _inFlight: null }),
}));
